import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { LASERCUT } from "@/constants/testIds";
import { buildWhatsappLink } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: "gazebo", label: "Gazebo & Pergola" },
  { slug: "furniture", label: "Kursi & Furniture" },
  { slug: "bbq", label: "BBQ Grill & Fire Pit" },
  { slug: "fence", label: "Pagar & Partisi" },
  { slug: "wallart", label: "Wall Art & Signage" },
  { slug: "table", label: "Meja & Planter Stand" },
];

const BUDGETS = [
  "< Rp5 juta",
  "Rp5 - 15 juta",
  "Rp15 - 50 juta",
  "> Rp50 juta",
  "Belum tahu, minta estimasi",
];

export const CustomRequest = ({ presetCategory }) => {
  const { register, handleSubmit, control, reset, setValue, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { name: "", whatsapp: "", category: "", size: "", budget_range: "", description: "" },
  });

  useEffect(() => {
    if (presetCategory) setValue("category", presetCategory);
  }, [presetCategory, setValue]);

  const onSubmit = async (data) => {
    const categoryObj = CATEGORIES.find((c) => c.slug === data.category);
    try {
      await axios.post(`${API}/leads`, {
        name: data.name,
        whatsapp: data.whatsapp,
        category: data.category,
        category_label: categoryObj?.label || data.category,
        size: data.size,
        budget_range: data.budget_range,
        description: data.description,
      });
      toast.success("Permintaan terkirim! Melanjutkan ke WhatsApp...");
      const link = buildWhatsappLink({ ...data, category_label: categoryObj?.label });
      window.open(link, "_blank");
      reset();
    } catch (err) {
      console.error(err);
      toast.error("Gagal mengirim permintaan. Silakan coba lagi.");
    }
  };

  return (
    <section id="konsultasi" className="relative bg-ink py-24 md:py-36 px-6 md:px-12 lg:px-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-brass">Custom Request</span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight text-chromed mt-4 leading-tight">
            Ceritakan kebutuhan Anda, kami wujudkan dalam logam.
          </h2>
          <p className="text-chromed/60 mt-6 max-w-md leading-relaxed">
            Isi form berikut, tim kami akan menghubungi Anda via WhatsApp untuk konsultasi lebih
            lanjut — ukuran, motif, dan estimasi biaya.
          </p>
        </div>

        <motion.form
          data-testid={LASERCUT.formSection}
          onSubmit={handleSubmit(onSubmit)}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="space-y-6 border border-linecol bg-surfacedark p-8 md:p-10"
        >
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-chromed/60 mb-2 block">Nama</label>
            <Input
              data-testid={LASERCUT.formName}
              {...register("name", { required: true })}
              placeholder="Nama lengkap"
              className="rounded-none border-0 border-b border-linecol bg-transparent focus-visible:ring-0 focus-visible:border-brass px-0"
            />
            {errors.name && <p className="text-xs text-destructive mt-1">Nama wajib diisi</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-chromed/60 mb-2 block">Nomor WhatsApp</label>
            <Input
              data-testid={LASERCUT.formWhatsapp}
              {...register("whatsapp", { required: true })}
              placeholder="08xx-xxxx-xxxx"
              className="rounded-none border-0 border-b border-linecol bg-transparent focus-visible:ring-0 focus-visible:border-brass px-0"
            />
            {errors.whatsapp && <p className="text-xs text-destructive mt-1">Nomor WhatsApp wajib diisi</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-chromed/60 mb-2 block">Kategori Produk</label>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger
                    data-testid={LASERCUT.formCategory}
                    className="rounded-none border-0 border-b border-linecol bg-transparent focus:ring-0 px-0"
                  >
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent className="bg-surfacedark border-linecol text-chromed">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.category && <p className="text-xs text-destructive mt-1">Pilih kategori produk</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-chromed/60 mb-2 block">Ukuran (opsional)</label>
            <Input
              data-testid={LASERCUT.formSize}
              {...register("size")}
              placeholder="misal: 3m x 2m"
              className="rounded-none border-0 border-b border-linecol bg-transparent focus-visible:ring-0 focus-visible:border-brass px-0"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-chromed/60 mb-2 block">Budget Range</label>
            <Controller
              name="budget_range"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger
                    data-testid={LASERCUT.formBudget}
                    className="rounded-none border-0 border-b border-linecol bg-transparent focus:ring-0 px-0"
                  >
                    <SelectValue placeholder="Pilih kisaran budget" />
                  </SelectTrigger>
                  <SelectContent className="bg-surfacedark border-linecol text-chromed">
                    {BUDGETS.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.budget_range && <p className="text-xs text-destructive mt-1">Pilih kisaran budget</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-chromed/60 mb-2 block">Deskripsi Kebutuhan</label>
            <Textarea
              data-testid={LASERCUT.formDescription}
              {...register("description", { required: true })}
              placeholder="Ceritakan motif, warna, atau referensi yang Anda inginkan..."
              rows={4}
              className="rounded-none border border-linecol bg-transparent focus-visible:ring-0 focus-visible:border-brass"
            />
            {errors.description && <p className="text-xs text-destructive mt-1">Deskripsi wajib diisi</p>}
          </div>

          <Button
            data-testid={LASERCUT.formSubmit}
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-none bg-brass text-ink hover:bg-brass-light h-12 font-medium tracking-wide"
          >
            {isSubmitting ? "Mengirim..." : "Kirim & Lanjut ke WhatsApp"}
          </Button>
        </motion.form>
      </div>
    </section>
  );
};
